import { Logo } from '../lib/ui'
import {
  APPEALS_PROCESS,
  COMMUNITY_RULES_EFFECTIVE_DATE,
  COMMUNITY_RULES_PREAMBLE,
  COMMUNITY_RULES_VERSION,
  COMMUNITY_RULE_SECTIONS,
  ENFORCEMENT_SEVERITY,
  ENFORCEMENT_TRANSPARENCY,
  STANDARDS_CHANGELOG,
  STANDARDS_RETROACTIVITY,
} from '../../shared/community-rules'

type Props = {
  backHref?: string
  showNav?: boolean
}

export function SafetyRules({ backHref = '#/', showNav = true }: Props) {
  return (
    <main className="landing-shell">
      {showNav && (
        <header className="landing-nav">
          <Logo />
          <nav>
            <a href="#/signin">Sign in</a>
            <a className="button primary" href="#/signup">
              Create account
            </a>
          </nav>
        </header>
      )}

      <div className="static-page rules-page">
        {backHref && (
          <a className="back-link" href={backHref}>
            ← Back
          </a>
        )}

        <span className="eyebrow">Safety &amp; rules</span>
        <h1>{COMMUNITY_RULES_PREAMBLE.headline}</h1>
        <p className="page-sub rules-lead">{COMMUNITY_RULES_PREAMBLE.lead}</p>
        <p className="rules-moderation">{COMMUNITY_RULES_PREAMBLE.moderation}</p>
        <p className="rules-equal">{COMMUNITY_RULES_PREAMBLE.equalApplication}</p>
        <p className="rules-version">
          Effective {COMMUNITY_RULES_EFFECTIVE_DATE} · version {COMMUNITY_RULES_VERSION} · applies equally to every
          member
        </p>

        <section className="rules-block" id="revisions">
          <h2>Standards revisions</h2>
          <p className="section-intro">{STANDARDS_RETROACTIVITY}</p>
          <ul className="rules-list changelog-list">
            {STANDARDS_CHANGELOG.map((rev) => (
              <li key={rev.version}>
                <strong>{rev.version}</strong> ({rev.effectiveDate}) — {rev.summary}
              </li>
            ))}
          </ul>
        </section>

        <section className="rules-block">
          <h2>{ENFORCEMENT_TRANSPARENCY.title}</h2>
          <ul className="rules-list">
            {ENFORCEMENT_TRANSPARENCY.points.map((point) => (
              <li key={point.slice(0, 48)}>{point}</li>
            ))}
          </ul>
        </section>

        <section className="rules-block" id="enforcement-severity">
          <h2>{ENFORCEMENT_SEVERITY.title}</h2>
          <p className="section-intro">{ENFORCEMENT_SEVERITY.intro}</p>
          <dl className="severity-factors">
            {ENFORCEMENT_SEVERITY.factors.map((f) => (
              <div key={f.name}>
                <dt>{f.name}</dt>
                <dd>{f.detail}</dd>
              </div>
            ))}
          </dl>
          <p className="rules-note">{ENFORCEMENT_SEVERITY.note}</p>
        </section>

        {COMMUNITY_RULE_SECTIONS.map((section) => (
          <section key={section.id} className="rules-block" id={section.id}>
            <h2>{section.title}</h2>
            {section.intro && <p className="section-intro">{section.intro}</p>}
            <div className="rules-grid">
              {section.rules.map((rule) => (
                <article key={rule.id} className="rule-card" id={rule.id}>
                  <header className="rule-card-header">
                    <span className="rule-id">{rule.id}</span>
                    <h3>{rule.title}</h3>
                  </header>
                  <p className="rule-summary">{rule.summary}</p>
                  <p className="rule-text">{rule.rule}</p>
                  <div className="rule-examples">
                    <h4>Examples</h4>
                    <ul>
                      {rule.examples.map((ex) => (
                        <li key={ex.text.slice(0, 30)} className={ex.ok ? 'example-ok' : 'example-bad'}>
                          <strong>{ex.label}</strong> — {ex.text}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="rule-actions">
                    <h4>Typical enforcement</h4>
                    <ul>
                      {rule.typicalActions.map((action) => (
                        <li key={action}>{action}</li>
                      ))}
                    </ul>
                  </div>
                </article>
              ))}
            </div>
          </section>
        ))}

        <section className="rules-block" id="appeals">
          <h2>{APPEALS_PROCESS.title}</h2>
          <ol className="rules-appeal-steps">
            {APPEALS_PROCESS.steps.map((step) => (
              <li key={step.slice(0, 40)}>{step}</li>
            ))}
          </ol>
          <p className="rules-note">{APPEALS_PROCESS.timelines}</p>
        </section>

        <div className="rules-footer-cta">
          <p>Questions? <a href="mailto:support@pairvelle.com">support@pairvelle.com</a></p>
          <a className="button secondary" href="#/signin">
            Sign in to view your account standing
          </a>
        </div>
      </div>

      {showNav && (
        <footer className="landing-footer">
          <nav>
            <a href="#/legal/community">Safety &amp; rules</a>
            <a href="#/legal/privacy">Privacy</a>
            <a href="#/legal/terms">Terms</a>
            <a href="mailto:support@pairvelle.com">Support</a>
            <a href="#/">Home</a>
          </nav>
          <small>© {new Date().getFullYear()} Pairvelle · 18+ only</small>
        </footer>
      )}
    </main>
  )
}
